import { makeAutoObservable, runInAction } from "mobx";

const API_URL = import.meta.env.VITE_API_URL;

export default class ProfilStore {
  _id = null;

  _pseudo = "";
  _token = localStorage.getItem("token") || "";
  _mail = "";
  _score = 0;
  _isConnected = false;
  _profilPicture = "";
  _theme = {
    color: [
      [86, 12, 102],
      [112, 143, 255],
    ],
    name: "Space",
  };
  _admin = false;

  constructor() {
    makeAutoObservable(this);
  }

  // --- GETTERS ---
  get id() { return this._id; }
  get pseudo() { return this._pseudo; }
  get token() { return this._token; }
  get mail() { return this._mail; }
  get score() { return this._score; }
  get isConnected() { return this._isConnected; }
  get profilPicture() { return this._profilPicture; }
  get theme() { return this._theme; }
  get admin() { return this._admin; }

  // --- SETTERS ---
  set id(id) { this._id = id; }
  set pseudo(pseudo) { this._pseudo = pseudo; }
  set token(token) { this._token = token; }
  set mail(mail) { this._mail = mail; }
  set score(score) { this._score = score; }
  set isConnected(isConnected) { this._isConnected = isConnected; }
  set profilPicture(profilPicture) { this._profilPicture = profilPicture; }
  set theme(theme) { this._theme = theme; }
  set admin(admin) { this._admin = admin; }

  // --- AUTH METHODS ---

  async connect(identifier, password) {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur de connexion");
      }

      runInAction(() => {
        this._token = data.token;
        localStorage.setItem("token", data.token);
        this._id = data.user.id;
        this._pseudo = data.user.pseudo;
        this._mail = data.user.mail;
        this._admin = data.user.admin;
        this._score = data.user.score ?? 0;
        this._profilPicture = data.user.profilPicture ?? "";
        if (data.user.theme) {
          try {
            this._theme = typeof data.user.theme === "string" ? JSON.parse(data.user.theme) : data.user.theme;
          } catch {
            // garde le thème par défaut si parse échoue
          }
        }
        this._isConnected = true;
      });

      return data;
    } catch (error) {
      console.error("Erreur lors de la connexion :", error.message);
      throw error;
    }
  }

  async sign(pseudo, mail, password) {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pseudo, mail, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur lors de l’inscription");
      }

      // Après inscription, on connecte directement l'utilisateur
      return await this.connect(pseudo, password);
    } catch (err) {
      console.error("Erreur lors de l’inscription :", err.message);
      throw err;
    }
  }

  // --- PROFILE UPDATES (UTILISENT LE JWT) ---

  // --- MÉTHODE PRIVÉE POUR CENTRALISER LES PATCH /me ---
  async _patchMe(payload) {
    if (!this._token) return null;

    const res = await fetch(`${API_URL}/auth/me`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || "Erreur lors de la mise à jour");
    }

    return data;
  }

  async fetchMe() {
    const token = localStorage.getItem("token");
    if (!token) return;
    this._token = token;

    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this._token}`,
        },
      });

      const user = await res.json();
      if (!res.ok) throw new Error(user.error || "Erreur récupération profil");

      runInAction(() => {
        this._id = user.id;
        this._pseudo = user.pseudo;
        this._mail = user.mail;
        this._score = user.score ?? 0;
        this._profilPicture = user.profilPicture ?? "";
        this._admin = user.admin ?? false;
        if (user.theme) {
          try {
            this._theme = typeof user.theme === "string" ? JSON.parse(user.theme) : user.theme;
          } catch {
            // Conserve le thème par défaut si parse échoue
          }
        }
        this._isConnected = true;
      });
    } catch (err) {
      console.error("Erreur fetchMe:", err.message);
      // En cas de token invalide/expiré, déconnecter
      this.disconnect();
    }
  }

  // --- ACTIONS PATCH /me ---

  async updateScore(scoreToAdd) {
    const newScore = this._score + scoreToAdd;
    if (typeof newScore !== "number") return;

    try {
      await this._patchMe({ score: newScore });

      runInAction(() => {
        this._score = newScore;
      });
    } catch (err) {
      console.error("Erreur updateScore:", err.message);
    }
  }

  async updateProfilPicture(newProfilPicture) {
    if (typeof newProfilPicture !== "string") return;

    try {
      await this._patchMe({ profilPicture: newProfilPicture });

      runInAction(() => {
        this._profilPicture = newProfilPicture;
      });
    } catch (err) {
      console.error("Erreur updateProfilPicture:", err.message);
    }
  }

  async updateTheme(newTheme) {
    const themeStr = typeof newTheme === "string" ? newTheme : JSON.stringify(newTheme);

    try {
      await this._patchMe({ theme: themeStr });

      runInAction(() => {
        this._theme = newTheme;
      });
    } catch (err) {
      console.error("Erreur updateTheme:", err.message);
    }
  }

  async editProfil({ newPseudo = this._pseudo, newMail = this._mail }) {
    try {
      await this._patchMe({ pseudo: newPseudo, mail: newMail });

      runInAction(() => {
        this._pseudo = newPseudo;
        this._mail = newMail;
      });
    } catch (err) {
      console.error("Erreur editProfil:", err.message);
    }
  }

  disconnect() {
    localStorage.removeItem("token");
    runInAction(() => {
      this._id = null;
      this._pseudo = "";
      this._mail = "";
      this._token = "";
      this._score = 0;
      this._isConnected = false;
      this._profilPicture = "";
      this._admin = false;
      this._theme = {
        color: [
          [86, 12, 102],
          [112, 143, 255],
        ],
        name: "Space",
      };
    });
  }
}